import PaginationControls from '@/components/pagination/PaginationControls'
import { GroupedReportRow } from '@/lib/api/reports/reports-api'
import { useEffect, useMemo, useState } from 'react'
import { MdFullscreen, MdFullscreenExit, MdWarning } from 'react-icons/md'
import { ColumnDef, formatCurrency } from '../config/report-configs'

const PAGE_SIZES = [25, 50, 100, 200]

/**
 * Colunas do modo agrupado. As chaves espelham `GROUPED_COLUMN_DEFS` do backend
 * — é o que faz o menu "Colunas" recortar a tela e o arquivo do mesmo jeito.
 */
export const GROUPED_COLUMNS: ColumnDef[] = [
    { key: 'name', label: 'Item' },
    { key: 'count', label: 'Total', align: 'right' },
    // Só difere de "Total" quando o mesmo item entra com quantidade > 1 no
    // atendimento (2 unidades do mesmo exame na mesma requisição). Como isso é
    // raro, as duas colunas costumam mostrar o mesmo número — daí o tooltip.
    { key: 'appointments', label: 'Atendimentos', align: 'right' },
    { key: 'grossSale', label: 'Venda', align: 'right' },
    { key: 'discountAmount', label: 'Desconto', align: 'right' },
    { key: 'netValue', label: 'Valor', align: 'right' },
    { key: 'cost', label: 'Custo', align: 'right' },
    { key: 'profit', label: 'Lucro', align: 'right' },
]

/** Explicação de cada coluna, no `title` do cabeçalho. */
const COLUMN_HINTS: Record<string, string> = {
    name: 'Item do catálogo. A variação, quando há, entra entre parênteses.',
    count:
        'Quantidade total do item no período: 25 hemogramas, ainda que espalhados em 20 atendimentos.',
    appointments:
        'Atendimentos distintos em que o item apareceu. Só difere de "Total" quando o mesmo item entra com quantidade maior que 1 no mesmo atendimento.',
    grossSale: 'Preço congelado do item (unitário × quantidade). Valor exato.',
    discountAmount: 'Venda menos o valor: o desconto do atendimento, rateado.',
    netValue:
        'Receita líquida atribuída ao item (sem a taxa do gateway), rateada na proporção da venda.',
    cost: 'Custo apurado do atendimento, rateado pelo mesmo critério.',
    profit: 'Valor menos custo.',
}

interface GroupedReportTableProps {
    data: GroupedReportRow[]
    loading: boolean
    truncated: boolean
    /** Chaves visíveis, na ordem canônica. */
    visibleColumns: string[]
}

/**
 * O relatório somado por item de catálogo.
 *
 * Não é clicável como a tabela detalhada: a linha aqui é um item somado em
 * vários atendimentos, e não haveria um registro para abrir.
 */
export default function GroupedReportTable({
    data,
    loading,
    truncated,
    visibleColumns,
}: GroupedReportTableProps) {
    const columns = GROUPED_COLUMNS.filter(c => visibleColumns.includes(c.key))

    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [isFullscreen, setIsFullscreen] = useState(false)

    // Filtro novo, lista nova: manter a página 7 de uma lista que encolheu para
    // duas seria uma tela vazia sem explicação.
    useEffect(() => {
        setPage(1)
    }, [data, pageSize])

    useEffect(() => {
        if (!isFullscreen) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsFullscreen(false)
        }
        window.addEventListener('keydown', onKeyDown)
        // Trava o scroll da página atrás do overlay.
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [isFullscreen])

    const pageRows = useMemo(
        () => data.slice((page - 1) * pageSize, page * pageSize),
        [data, page, pageSize]
    )

    // Sem nenhum custo apurado no grupo, "R$ 0,00" mentiria — o custo não é
    // zero, é desconhecido. Mesma regra do export.
    const unknownCost = (row: GroupedReportRow) =>
        row.costUnknownCount > 0 && row.cost === 0

    const cell = (row: GroupedReportRow, key: string) => {
        switch (key) {
            case 'name':
                return row.name
            case 'count':
                return row.count.toLocaleString('pt-BR')
            case 'appointments':
                return row.appointments.toLocaleString('pt-BR')
            case 'grossSale':
                return formatCurrency(row.grossSale)
            case 'discountAmount':
                return formatCurrency(row.discountAmount)
            case 'netValue':
                return formatCurrency(row.netValue)
            case 'cost':
                return unknownCost(row) ? '—' : formatCurrency(row.cost)
            case 'profit':
                return unknownCost(row) ? '—' : formatCurrency(row.profit)
            default:
                return '—'
        }
    }

    const totals = data.reduce(
        (acc, row) => ({
            count: acc.count + row.count,
            grossSale: acc.grossSale + row.grossSale,
            discountAmount: acc.discountAmount + row.discountAmount,
            netValue: acc.netValue + row.netValue,
            cost: acc.cost + row.cost,
            profit: acc.profit + row.profit,
        }),
        { count: 0, grossSale: 0, discountAmount: 0, netValue: 0, cost: 0, profit: 0 }
    )

    const totalCell = (key: string) => {
        switch (key) {
            case 'name':
                return `${data.length} ${data.length === 1 ? 'item' : 'itens'}`
            case 'count':
                return totals.count.toLocaleString('pt-BR')
            case 'appointments':
                // Um atendimento com três itens aparece nos três grupos: somar a
                // coluna daria mais que o total de atendimentos do período.
                return '—'
            case 'grossSale':
                return formatCurrency(totals.grossSale)
            case 'discountAmount':
                return formatCurrency(totals.discountAmount)
            case 'netValue':
                return formatCurrency(totals.netValue)
            case 'cost':
                return formatCurrency(totals.cost)
            case 'profit':
                return formatCurrency(totals.profit)
            default:
                return ''
        }
    }

    if (loading && data.length === 0) {
        return (
            <div className="flex items-center justify-center h-48 rounded-xl border border-base-300/50 bg-base-100">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (data.length === 0) {
        return (
            <div className="flex items-center justify-center h-48 rounded-xl border border-base-300/50 bg-base-100 text-sm text-base-content/50">
                Nenhum item no período.
            </div>
        )
    }

    const showingFrom = data.length === 0 ? 0 : (page - 1) * pageSize + 1
    const showingTo = Math.min(page * pageSize, data.length)

    return (
        <div
            className={
                isFullscreen
                    ? 'fixed inset-0 z-[999] bg-base-100 flex flex-col p-4 gap-2'
                    : 'flex flex-col gap-2'
            }
        >
            <p className="text-xs text-base-content/50">
                Venda é o preço congelado de cada item, e
                valor, desconto e custo são rateados entre os itens do atendimento, na
                proporção da venda. Os totais fecham com os cards acima.
            </p>

            {truncated && (
                <div className="flex items-center gap-2 text-xs text-warning">
                    <MdWarning />
                    Recorte parcial: o período tem mais atendimentos do que o teto
                    de leitura. Reduza o período para o total fechar.
                </div>
            )}

            <div
                className={`rounded-xl border border-base-300/50 bg-base-100 flex flex-col overflow-hidden ${
                    isFullscreen ? 'flex-1' : ''
                }`}
            >
                <div className="flex-shrink-0 px-5 py-3 border-b border-base-300/50 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm text-base-content/70">
                        Exibindo {showingFrom}-{showingTo} de {data.length}{' '}
                        {data.length === 1 ? 'item' : 'itens'}
                    </p>
                    <div className="flex items-center gap-3 text-sm">
                        <select
                            className="select select-bordered select-sm"
                            value={pageSize}
                            onChange={e => setPageSize(Number(e.target.value))}
                        >
                            {PAGE_SIZES.map(size => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        {/* Nome de item de catálogo é longo ("Hemograma completo
                            com contagem de plaquetas ou frações…"); em tela cheia
                            ele cabe sem quebrar em três linhas. */}
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost gap-1"
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={
                                isFullscreen
                                    ? 'Sair do modo expandido (Esc)'
                                    : 'Modo expandido'
                            }
                        >
                            {isFullscreen ? (
                                <MdFullscreenExit className="text-lg" />
                            ) : (
                                <MdFullscreen className="text-lg" />
                            )}
                            <span className="hidden sm:inline text-xs">
                                {isFullscreen ? 'Sair' : 'Expandir'}
                            </span>
                        </button>
                    </div>
                </div>

                <div
                    className={`overflow-auto ${isFullscreen ? 'flex-1' : ''} ${
                        loading ? 'pointer-events-none opacity-50' : ''
                    }`}
                >
                    <table className="table table-sm table-pin-rows">
                        <thead>
                            <tr>
                                {columns.map(column => (
                                    <th
                                        key={column.key}
                                        className={
                                            column.align === 'right'
                                                ? 'text-right'
                                                : ''
                                        }
                                        title={COLUMN_HINTS[column.key]}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map(row => (
                                <tr key={row.catalogId ?? row.name}>
                                    {columns.map(column => (
                                        <td
                                            key={column.key}
                                            className={
                                                column.align === 'right'
                                                    ? 'text-right whitespace-nowrap'
                                                    : ''
                                            }
                                        >
                                            {cell(row, column.key)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {/* Os totais são de TODOS os itens do período, não só
                                da página — é o número que fecha com os cards. */}
                            <tr className="font-semibold">
                                {columns.map(column => (
                                    <td
                                        key={column.key}
                                        className={
                                            column.align === 'right'
                                                ? 'text-right whitespace-nowrap'
                                                : ''
                                        }
                                    >
                                        {totalCell(column.key)}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {data.length > pageSize && (
                    <div className="flex-shrink-0 border-t border-base-300/50 px-5 py-3">
                        <PaginationControls
                            page={page}
                            limit={pageSize}
                            total={data.length}
                            loading={loading}
                            onPageChange={setPage}
                            label="item"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
